import { useState, useMemo, useCallback, useEffect, type CSSProperties } from 'react';

import {
  BellOutlined,
  EnvironmentOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  Typography,
  Button,
  Space,
  Layout,
  Tag,
  Checkbox,
  Input,
  Tooltip,
  Modal,
  Drawer,
  DatePicker,
  Select,
  Dropdown,
  Tabs,
  Table,
  Pagination,
} from 'antd';
import Calendar from 'antd/es/calendar';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useMediaQuery } from 'react-responsive';

import '@/features/agenda/styles/agenda.less';
import { ReactComponent as CloseItemIcon } from '@/assets/images/close-item.svg';
import { DayColumn } from '@/features/calendar/components/DayColumn';
import { DraggableEvent } from '@/features/calendar/components/DraggableEvent';
import { EventDetailsModal } from '@/features/calendar/components/feedback/EventDetailsModal';
import { SupportRequestDrawer } from '@/features/calendar/components/feedback/SupportRequestDrawer';
import { renderMiniHeader } from '@/features/calendar/components/renderMiniHeader';
import { CalendarEventType } from '@/features/calendar/entities/events';
import { useCalendarEvents, type AdminEvent } from '@/features/calendar/hooks/useCalendarEvents';
import styles from '@/features/calendar/styles/calendar.module.less';
import { useCompanies } from '@/features/company/api/getCompanies';
import { useImpactEvents } from '@/features/irm/api/getImpactEvents';
import { EventSchema } from '@/features/irm/entities/events';
import { ListShareholderWithGroupSchema } from '@/features/irm/entities/participants';
import { useCurrentCompanyId } from '@/hooks/useCurrentCompanyId';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { queryClient } from '@/lib/react-query';
import { useThemeStore } from '@/stores/useThemeStore';
import { useIsAdmin } from '@/utils/isAdmin';

import { useCreateEvent } from '@irm/api/createEvent';
import { useDeleteEvent } from '@irm/api/deleteEvent';
import {
  useShareholderWithGroup,
  ShareholderWithGroupFilters,
} from '@irm/api/getListShareholderWithGroup';
import { useShareholdersEvents } from '@irm/api/getShareholderByEvents';
import { useUpdateEvent } from '@irm/api/updateEvent';

const { Content } = Layout;

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale('pt-br');

type Category = { id: string; name: string; color: string };

const toUTCMinus3 = (date: Dayjs) => date.utcOffset(-3, true);

const START_HOUR = 0;
const END_HOUR = 24;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }).map((_, i) => START_HOUR + i);
const HOUR_ROW_HEIGHT = 35;
const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const DAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const getAgendaColor = (agendaName: string): string => {
  const name = agendaName.toLowerCase();

  if (name.includes('acionista') || name.includes('shareholder')) {
    return '#a8a8a8'; // Azul para Acionistas
  }
  if (name.includes('setor') || name.includes('mercado')) {
    return ' #003d86'; // Roxo para Setor/Mercado
  }
  if (name.includes('companhia') || name.includes('company')) {
    return '#5c8ef9'; // Cinza para Companhia
  }
  if (name.includes('regulatório') || name.includes('cvm') || name.includes('b3')) {
    return '#FFB030'; // Amarelo para Regulatório CVM/B3
  }

  return '#005462';
};

const getEventColor = (impact: { id: number; name: string }) => {
  const agendaColor = getAgendaColor(impact.name);

  return {
    bg: '#fff',
    border: agendaColor,
    text: '#262626',
  };
};

const getEventTagColor = (impact: { id: number; name: string }) => {
  const name = impact.name.toLowerCase();

  if (name.includes('acionista') || name.includes('shareholder')) {
    return 'blue'; // Azul para Acionistas
  }
  if (name.includes('setor') || name.includes('mercado')) {
    return 'purple'; // Roxo para Setor/Mercado
  }
  if (name.includes('companhia') || name.includes('company')) {
    return 'default'; // Cinza para Companhia
  }
  if (name.includes('regulatório') || name.includes('cvm') || name.includes('b3')) {
    return 'default'; // Cinza para Regulatório CVM/B3
  }

  return 'blue'; // Cor padrão
};

const getOrderIndex = (name: string) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('acionista')) return 0;
  if (lowerName.includes('companhia')) return 1;
  if (lowerName.includes('setor') || lowerName.includes('mercado')) return 2;
  if (lowerName.includes('regulatório') || lowerName.includes('cvm') || lowerName.includes('b3'))
    return 3;
  return 999;
};

export const CalendarAgenda = () => {
  const theme = useThemeStore((state) => state.theme);
  const isDarkMode = theme === 'dark';
  const isAdmin = useIsAdmin();
  const BORDER = isDarkMode ? '#333' : '#f7f7f7';
  const HEADER_BG = isDarkMode ? '#08444E' : '#f7f7f7';

  const isMobile = useMediaQuery({ maxWidth: 900 });
  const isTablet = useMediaQuery({ minWidth: 901, maxWidth: 1199 });
  const sidebarWidth = isMobile ? '100%' : isTablet ? '280px' : '320px';
  const sidebarNumericWidth = isMobile ? 0 : isTablet ? 280 : 320;
  const agendaContentMaxWidth = isMobile ? '100%' : `calc(100% - ${sidebarNumericWidth + 16}px)`;

  const [categories, setCategories] = useState<Category[]>([]);
  const [events] = useState<AdminEvent[]>([]);
  const [weekStart, setWeekStart] = useState(dayjs().startOf('week').add(1, 'day'));
  const [view, setView] = useState<'week' | 'day' | 'month'>('week');
  const [activeDate, setActiveDate] = useState<Dayjs>(dayjs());
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedImpacts, setSelectedImpacts] = useState<number[]>([]);

  const [nowMinutes] = useState(() => (dayjs().hour() - START_HOUR) * 60 + dayjs().minute());
  const [searchTerm, setSearchTerm] = useState('');

  const UpdatesDropdownContent = useMemo(
    () => (
      <div
        style={{
          backgroundColor: 'var(--background-secondary)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--border-color)',
          padding: '16px',
          width: '380px',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        <div className={styles.updatesModalHeader}>
          <Typography.Title level={5} className={styles.updatesModalTitle}>
            Atualizações
          </Typography.Title>
          <Typography.Text type="secondary" className={styles.updatesModalSubtitle}>
            Últimas informações
          </Typography.Text>
        </div>

        <div className={styles.updatesModalContent}>
          <div className={styles.updateCard}>
            <div className={styles.updateCardHeader}>
              <Typography.Text strong className={styles.updateCardTitle}>
                Lorem ipsum dolor sit amet
              </Typography.Text>
              <Typography.Text type="secondary" className={styles.updateCardDate}>
                Hoje
              </Typography.Text>
            </div>
            <Typography.Paragraph className={styles.updateCardDescription}>
              Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore
              magna aliqua.
            </Typography.Paragraph>
            <Typography.Text type="secondary" className={styles.updateCardTime}>
              14:30
            </Typography.Text>
          </div>

          <div className={styles.updateCard}>
            <div className={styles.updateCardHeader}>
              <Typography.Text strong className={styles.updateCardTitle}>
                Ut enim ad minim veniam
              </Typography.Text>
              <Typography.Text type="secondary" className={styles.updateCardDate}>
                Ontem
              </Typography.Text>
            </div>
            <Typography.Paragraph className={styles.updateCardDescription}>
              Quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
            </Typography.Paragraph>
            <Typography.Text type="secondary" className={styles.updateCardTime}>
              08:00
            </Typography.Text>
          </div>

          <div className={styles.updateCard}>
            <div className={styles.updateCardHeader}>
              <Typography.Text strong className={styles.updateCardTitle}>
                Duis aute irure dolor
              </Typography.Text>
              <Typography.Text type="secondary" className={styles.updateCardDate}>
                2 dias
              </Typography.Text>
            </div>
            <Typography.Paragraph className={styles.updateCardDescription}>
              In reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
            </Typography.Paragraph>
            <Typography.Text type="secondary" className={styles.updateCardTime}>
              16:45
            </Typography.Text>
          </div>
        </div>
      </div>
    ),
    [],
  );

  const getTitleText = () => {
    if (view === 'week') {
      const weekEnd = weekStart.add(6, 'day');
      const startDay = weekStart.locale('pt-br').format('DD');
      const endDay = weekEnd.locale('pt-br').format('DD');
      const month = weekStart.locale('pt-br').format('MMMM');
      const year = weekStart.locale('pt-br').format('YYYY');
      return `${startDay} - ${endDay} de ${month} de ${year}`;
    }
    if (view === 'day') {
      return activeDate.locale('pt-br').format('D [de] MMMM [de] YYYY');
    }
    return activeDate.locale('pt-br').format('MMMM [de] YYYY');
  };

  const [selectedEventForDetails, setSelectedEventForDetails] = useState<CalendarEventType | null>(
    null,
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSupportDrawerOpen, setIsSupportDrawerOpen] = useState(false);
  const [supportEvent, setSupportEvent] = useState<CalendarEventType>();

  // estados para criação de eventos
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formStartDate, setFormStartDate] = useState<Dayjs | null>(dayjs());
  const [formStartTime, setFormStartTime] = useState<Dayjs | null>(dayjs().hour(0).minute(0));
  const [formEndDate, setFormEndDate] = useState<Dayjs | null>(dayjs());
  const [formEndTime, setFormEndTime] = useState<Dayjs | null>(dayjs().hour(23).minute(59));
  const [formIsFullDay, setFormIsFullDay] = useState(false);
  const [formTimeType, setFormTimeType] = useState<
    'time' | 'before_market' | 'after_market' | 'full_day'
  >('full_day');
  const [formImpact, setFormImpact] = useState<number | undefined>(undefined);
  const [formEventType, setFormEventType] = useState<number | undefined>(undefined);

  // estados de validação
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    date?: string;
    impact?: string;
    eventType?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  }>({});

  // estados para criação de agendas
  const [isMarkerModalOpen, setIsMarkerModalOpen] = useState(false);
  const [newMarkerName, setNewMarkerName] = useState('');
  const [newMarkerColor, setNewMarkerColor] = useState('#005462');

  // estados para edição de eventos
  const [selectedEvent, setSelectedEvent] = useState<EventSchema | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // estados para paginação da lista de acionistas
  const [selectedShareholders, setSelectedShareholders] = useState<string[]>([]);
  const [searchShareholderTerm, setSearchShareholderTerm] = useState('');
  const debouncedSearchShareholderTerm = useDebouncedValue(searchShareholderTerm);
  const [shareholderFiltersParams, setShareholderFiltersParams] =
    useState<ShareholderWithGroupFilters>({
      search: '',
      page: 1,
      size: 15,
    });

  // estados para edição de agendas
  const [editingMarker, setEditingMarker] = useState<Category | null>(null);
  const [editMarkerName, setEditMarkerName] = useState('');
  const [editMarkerColor, setEditMarkerColor] = useState('#005462');

  const [isEditMarkerModalOpen, setIsEditMarkerModalOpen] = useState(false);

  // estado para modal de atualizações
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<EventSchema | null>(null);

  // estados para paginação das rotinas
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const {
    events: calendarEvents,
    irmEventsAsAdminEvents,
    refetch: refetchCalendarEvents,
  } = useCalendarEvents({
    selectedImpacts,
    isAdmin,
  });

  const companyId = useCurrentCompanyId();
  const impactEventsQuery = useImpactEvents({ companyId });
  const companiesQuery = useCompanies({
    filters: { page: '1', limit: '999' },
    config: { enabled: isAdmin },
  });

  const shareholdersWithGroupQuery = useShareholderWithGroup({
    companyId,
    filters: {
      search: debouncedSearchShareholderTerm,
      page: shareholderFiltersParams.page,
      size: shareholderFiltersParams.size,
    },
  });

  const companiesMap = useMemo(() => {
    const map = new Map<string, { name?: string; logoURL?: string }>();

    (companiesQuery.data?.data ?? []).forEach((companyData) => {
      map.set(companyData.company.id, {
        name: companyData.company.name,
        logoURL: companyData.company.more.logoURL,
      });
    });

    return map;
  }, [companiesQuery.data?.data]);

  // Queries de mutação para eventos
  const createEventQuery = useCreateEvent({
    config: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['events'], exact: false });
        queryClient.invalidateQueries(['impact-events', companyId]);

        const refetchResult = await refetchCalendarEvents();
        if (refetchResult) {
          await Promise.all(Array.isArray(refetchResult) ? refetchResult : [refetchResult]);
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 200);
        });
      },
    },
  });
  const updateEventQuery = useUpdateEvent({
    config: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['events'], exact: false });
        queryClient.invalidateQueries(['impact-events', companyId]);

        const refetchResult = await refetchCalendarEvents();
        if (refetchResult) {
          await Promise.all(Array.isArray(refetchResult) ? refetchResult : [refetchResult]);
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 200);
        });
      },
    },
  });
  const deleteEventQuery = useDeleteEvent({
    config: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['events'], exact: false });
        queryClient.invalidateQueries(['impact-events', companyId]);

        const refetchResult = await refetchCalendarEvents();
        if (refetchResult) {
          await Promise.all(Array.isArray(refetchResult) ? refetchResult : [refetchResult]);
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 200);
        });
      },
    },
  });

  // Query para buscar acionistas de um evento específico (para edição)
  const shareholdersEventsQuery = useShareholdersEvents({
    companyId,
    eventId: selectedEvent?.id || '',
    config: {
      onError: () => {
        setSelectedShareholders([]);
      },
    },
  });

  const getEventTypesForImpact = useMemo(() => {
    if (!impactEventsQuery.data?.data || !formImpact) return [];

    const selectedImpact = impactEventsQuery.data.data.find((impact) => impact.id === formImpact);
    return selectedImpact?.event_types || [];
  }, [impactEventsQuery.data?.data, formImpact]);

  const eventTypeOptions = useMemo(() => {
    const baseOptions = getEventTypesForImpact;

    if (
      selectedEvent &&
      selectedEvent.event_type &&
      formEventType &&
      !baseOptions.some((et) => et.id === formEventType)
    ) {
      return [
        ...baseOptions,
        {
          id: selectedEvent.event_type.id,
          name: selectedEvent.event_type.name,
          impact_id: selectedEvent.event_type.impact_id,
          company_id: selectedEvent.event_type.company_id,
        },
      ];
    }

    return baseOptions;
  }, [getEventTypesForImpact, formEventType, selectedEvent]);

  const shouldShowAcionistaTab = useMemo(() => {
    if (!impactEventsQuery.data?.data || !formImpact) return false;

    const selectedImpact = impactEventsQuery.data.data.find((impact) => impact.id === formImpact);
    return selectedImpact?.name?.toLowerCase().includes('acionista') || false;
  }, [impactEventsQuery.data?.data, formImpact]);

  const shareholders = useMemo(() => {
    return shareholdersWithGroupQuery.data?.data?.shareholders || [];
  }, [shareholdersWithGroupQuery.data?.data?.shareholders]);

  useEffect(() => {
    if (impactEventsQuery.data?.data && selectedImpacts.length === 0) {
      const allImpactIds = impactEventsQuery.data.data.map((impact) => impact.id);
      setSelectedImpacts(allImpactIds);
    }
  }, [impactEventsQuery.data?.data, selectedImpacts.length]);

  useEffect(() => {
    if (selectedEvent && isEditMode) {
      setFormTitle(selectedEvent.title || '');
      setFormDescription(selectedEvent.description);
      setFormLocation(selectedEvent.local || '');
      setFormImpact(selectedEvent.impact.id);
      setFormEventType(selectedEvent.event_type.id);

      const eventDate = dayjs(selectedEvent.start_date);
      setFormStartDate(eventDate);
      setFormStartTime(eventDate);

      if (selectedEvent.end_date) {
        const endDate = dayjs(selectedEvent.end_date);
        setFormEndDate(endDate);
        setFormEndTime(endDate);
      } else {
        setFormEndDate(null);
        setFormEndTime(null);
      }

      setFormIsFullDay(false);
      setFormTimeType(
        (selectedEvent.before_market || 'time') as
          | 'time'
          | 'before_market'
          | 'after_market'
          | 'full_day',
      );

      if (shareholdersEventsQuery.data?.data && Array.isArray(shareholdersEventsQuery.data?.data)) {
        setSelectedShareholders(shareholdersEventsQuery.data?.data);
      }
    }
  }, [selectedEvent, isEditMode, shareholdersEventsQuery.data?.data]);

  const mapCompaniesToEvent = useCallback(
    (event: CalendarEventType): CalendarEventType => {
      const companyIds =
        event.company_ids && event.company_ids.length > 0
          ? event.company_ids
          : event.company_id
          ? [event.company_id]
          : [];

      const companies =
        companyIds.length > 0
          ? companyIds.map((id) => {
              const companyData = companiesMap.get(id);
              return {
                id,
                name: companyData?.name || id,
                logoURL: companyData?.logoURL,
              };
            })
          : [];

      return {
        ...event,
        company_ids: companyIds,
        companies,
      };
    },
    [companiesMap],
  );

  const localEventsAsCalendarEvents: CalendarEventType[] = events.map((event) => {
    const impact = event.impact || { id: 1, name: 'Assembleia Geral' };
    const eventType = event.event_type || { id: 1, name: 'Assembleia' };

    return {
      id: event.id,
      title: event.title,
      description: event.description || '',
      start_date: event.start_date ? dayjs.utc(event.start_date).local() : dayjs(),
      end_date: event.end_date ? dayjs.utc(event.end_date).local() : undefined,
      local: event.local || '',
      before_market: event.before_market,
      company_id: 'local-agenda', // ID temporario pra dev mudar dps
      favorite: event.favorite || false,
      hide: event.hide || false,
      impact,
      event_type: {
        id: eventType.id,
        name: eventType.name,
        impact_id: impact.id,
        company_id: 'local-agenda',
      },
      origin: event.origin || 'Agenda',
      user: event.user
        ? {
            name: event.user.name,
            image_url: event.user.image_url || '',
          }
        : { name: 'Usuário', image_url: '' },
      shareholder: event.shareholder,
    } as CalendarEventType;
  });

  const calendarEventsWithCompanies = useMemo(
    () => calendarEvents.map(mapCompaniesToEvent),
    [calendarEvents, mapCompaniesToEvent],
  );

  const localEventsWithCompanies = useMemo(
    () => localEventsAsCalendarEvents.map(mapCompaniesToEvent),
    [localEventsAsCalendarEvents, mapCompaniesToEvent],
  );

  const allCalendarEvents = [...calendarEventsWithCompanies, ...localEventsWithCompanies]
    .filter((event) => {
      const today = dayjs().startOf('day');
      const eventStartDate = event.start_date.startOf('day');
      const eventEndDate = event.end_date ? event.end_date.startOf('day') : null;

      return (
        eventStartDate.isSameOrAfter(today) || (eventEndDate && eventEndDate.isSameOrAfter(today))
      );
    })
    .sort((a, b) => a.start_date.diff(b.start_date));

  const filteredEvents = useMemo(() => {
    const allEvents = [...events, ...irmEventsAsAdminEvents];
    let filtered = allEvents;

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((e) =>
        e.categoryId ? selectedCategories.includes(e.categoryId) : true,
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.title.toLowerCase().includes(searchLower) ||
          e.description?.toLowerCase().includes(searchLower) ||
          e.local?.toLowerCase().includes(searchLower),
      );
    }

    return filtered;
  }, [events, irmEventsAsAdminEvents, selectedCategories, searchTerm]);

  const getEventDate = useCallback((event: CalendarEventType | AdminEvent): Dayjs | null => {
    if ('date' in event && event.date && typeof event.date === 'string') {
      const date = dayjs(event.date);
      return date;
    }

    if ('start_date' in event && event.start_date) {
      if (typeof event.start_date === 'string') {
        const date = dayjs(event.start_date);
        return date;
      }
      if (event.start_date && typeof event.start_date.format === 'function') {
        return event.start_date;
      }
    }

    return null;
  }, []);

  const convertToAdminEvent = useCallback(
    (event: CalendarEventType | AdminEvent, _targetDate?: Dayjs): AdminEvent => {
      if ('startHour' in event) {
        return event as AdminEvent;
      }

      const eventDate = getEventDate(event);
      let startHour = eventDate ? eventDate.hour() : 9;
      let startMinute = eventDate ? eventDate.minute() : 0;

      let durationMinutes = 60;
      if (event.end_date && eventDate) {
        const endDate = dayjs(event.end_date);
        durationMinutes = Math.max(1, endDate.diff(eventDate, 'minute'));
      }

      const minHour = START_HOUR;
      const maxHour = END_HOUR;

      if (startHour < minHour) {
        startHour = minHour;
        startMinute = 0;
      } else if (startHour >= maxHour) {
        startHour = maxHour - 1;
        startMinute = 0;
      }

      const maxDuration = (maxHour - startHour) * 60 - startMinute;
      durationMinutes = Math.min(durationMinutes, maxDuration);

      return {
        id: event.id,
        title: event.title,
        description: event.description || '',
        dayIndex: 0,
        startHour,
        startMinute,
        durationMinutes,
        allDay: event.before_market === 'before_market' || event.before_market === 'after_market',
        categoryId: event.impact?.id?.toString(),
        local: event.local,
        favorite: event.favorite || false,
        hide: event.hide || false,
        start_date: eventDate?.format('YYYY-MM-DD'),
        end_date: event.end_date?.format('YYYY-MM-DD'),
        before_market: event.before_market,
        user: event.user || undefined,
        impact: event.impact,
        event_type: event.event_type,
        origin: event.origin,
        shareholder: event.shareholder
          ? {
              name: event.shareholder.name || '',
              quantity: event.shareholder.quantity,
            }
          : undefined,
        is_created_by_admin: 'is_created_by_admin' in event ? event.is_created_by_admin : undefined,
      };
    },
    [getEventDate],
  );

  const calculateEventLayout = useCallback(
    (dayEvents: (CalendarEventType | AdminEvent)[], targetDate?: Dayjs) => {
      const nonAllDayEvents = dayEvents.filter((event) => {
        if ('allDay' in event && event.allDay) return false;

        if ('startHour' in event && event.startHour < 0) return false;

        if ('before_market' in event) {
          const beforeMarket = event.before_market;
          if (
            beforeMarket === 'full_day' ||
            beforeMarket === 'before_market' ||
            beforeMarket === 'after_market'
          ) {
            return false;
          }
        }

        return true;
      });

      const adminEvents = nonAllDayEvents.map((event) => convertToAdminEvent(event, targetDate));

      const sortedEvents = [...adminEvents].sort((a, b) => {
        const timeA = a.startHour * 60 + a.startMinute;
        const timeB = b.startHour * 60 + b.startMinute;
        return timeA - timeB;
      });

      const groups: AdminEvent[][] = [];

      sortedEvents.forEach((event) => {
        const eventStart = event.startHour * 60 + event.startMinute;

        let addedToGroup = false;
        groups.forEach((group) => {
          const lastEvent = group[group.length - 1];
          const lastEventEnd =
            lastEvent.startHour * 60 + lastEvent.startMinute + lastEvent.durationMinutes;

          if (eventStart < lastEventEnd) {
            group.push(event);
            addedToGroup = true;
          }
        });

        if (!addedToGroup) {
          groups.push([event]);
        }
      });

      const layout: Array<
        AdminEvent & {
          left: number;
          width: number;
          zIndex: number;
          overlappingEvents?: AdminEvent[];
          isOverlapping?: boolean;
        }
      > = [];

      groups.forEach((group) => {
        const firstEvent = group[0];
        const overlappingEvents = group.slice(1);

        layout.push({
          ...firstEvent,
          left: 0,
          width: 100,
          zIndex: 1,
          overlappingEvents: overlappingEvents.length > 0 ? overlappingEvents : undefined,
          isOverlapping: overlappingEvents.length > 0,
        });
      });

      return layout;
    },
    [convertToAdminEvent],
  );

  const handleCloseSupportDrawer = () => {
    setIsSupportDrawerOpen(false);
    setSupportEvent(undefined);
  };

  const validateForm = () => {
    const errors: {
      title?: string;
      date?: string;
      impact?: string;
      eventType?: string;
      startTime?: string;
      endDate?: string;
      endTime?: string;
    } = {};

    if (!formTitle.trim()) {
      errors.title = 'Título é obrigatório';
    }

    if (!formStartDate) {
      errors.date = 'Data de início é obrigatória';
    }

    if (formTimeType === 'time' && !formStartTime) {
      errors.startTime = 'Hora de início é obrigatória';
    }

    if (!formImpact) {
      errors.impact = 'Agenda é obrigatória';
    }

    if (!formEventType) {
      errors.eventType = 'Tipo de evento é obrigatório';
    }

    if (formEndDate && formStartDate) {
      if (formTimeType !== 'time') {
        if (formEndDate.isBefore(formStartDate, 'day')) {
          errors.endDate = 'Data de término deve ser posterior à data de início';
        }
      } else if (formEndTime && formStartTime && formTimeType === 'time') {
        const startDateTime = dayjs(
          `${formStartDate.format('YYYY-MM-DD')} ${formStartTime.format('HH:mm')}`,
        );
        const endDateTime = dayjs(
          `${formEndDate.format('YYYY-MM-DD')} ${formEndTime.format('HH:mm')}`,
        );

        if (endDateTime.isSameOrBefore(startDateTime)) {
          errors.endTime = 'Data e hora de término devem ser posteriores à data e hora de início';
        }
      } else if (
        formEndDate.isSame(formStartDate, 'day') &&
        formStartTime &&
        !formEndTime &&
        formTimeType === 'time'
      ) {
        errors.endTime = 'Para o mesmo dia, defina uma hora de término posterior à hora de início';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetPagination = () => {
    setShareholderFiltersParams({
      search: '',
      page: 1,
      size: 15,
    });
    setSelectedShareholders([]);
    setSearchShareholderTerm('');
  };

  const resetForm = () => {
    setFormTitle('');
    setFormDescription('');
    setFormLocation('');
    setFormStartDate(dayjs());
    setFormStartTime(dayjs().hour(0).minute(0));
    setFormEndDate(dayjs());
    setFormEndTime(dayjs().hour(23).minute(59));
    setFormIsFullDay(false);
    setFormTimeType('full_day');
    setFormImpact(undefined);
    setFormEventType(undefined);
    setValidationErrors({});
    resetPagination();
  };

  const handleCellClick = (date: Dayjs, hour: number) => {
    const startDateTime = date.hour(hour).minute(0);
    const endDateTime = date.hour(hour + 1).minute(0);

    setFormStartDate(startDateTime);
    setFormStartTime(startDateTime);
    setFormEndDate(endDateTime);
    setFormEndTime(endDateTime);
    setFormTimeType('time');
    setFormIsFullDay(false);

    setFormTitle('');
    setFormDescription('');
    setFormLocation('');
    setFormImpact(undefined);
    setFormEventType(undefined);
    setValidationErrors({});

    setIsEditMode(false);
    setSelectedEvent(null);
    setIsCreateOpen(true);
  };

  const handleCreateEvent = () => {
    const now = dayjs();

    setFormStartDate(now);
    setFormStartTime(now);
    setFormEndDate(now.add(1, 'hour'));
    setFormEndTime(now.add(1, 'hour'));
    setFormTimeType('time');
    setFormIsFullDay(false);

    setFormTitle('');
    setFormDescription('');
    setFormLocation('');
    setFormImpact(undefined);
    setFormEventType(undefined);
    setValidationErrors({});

    setIsEditMode(false);
    setSelectedEvent(null);
    setIsCreateOpen(true);
  };

  const handleSave = async () => {
    if (!validateForm() || !formStartDate || !formImpact || !formEventType) {
      return;
    }

    if (!formTitle || formTitle.trim() === '') {
      setValidationErrors((prev) => ({ ...prev, title: 'Título é obrigatório' }));
      return;
    }

    let shareholdersToSave = selectedShareholders;

    if (selectedEvent?.id && shareholdersToSave.length === 0) {
      const shareholdersResponse = await shareholdersEventsQuery.refetch();
      shareholdersToSave = Array.isArray(shareholdersResponse.data?.data)
        ? (shareholdersResponse.data.data as string[])
        : [];
    }

    if (formImpact !== 1) {
      shareholdersToSave = [];
    }

    let formattedStartDate: string;
    let formattedEndDate: string | undefined;

    if (formTimeType === 'time') {
      const startHour = formStartTime?.format('HH:mm') || '09:00';
      const startDateTime = dayjs(`${formStartDate.format('YYYY-MM-DD')} ${startHour}:00`);
      formattedStartDate = toUTCMinus3(startDateTime).format('YYYY-MM-DDTHH:mm:ssZ');

      if (formEndDate && formEndTime) {
        const endHour = formEndTime.format('HH:mm');
        const endDateTime = dayjs(`${formEndDate.format('YYYY-MM-DD')} ${endHour}:00`);
        formattedEndDate = toUTCMinus3(endDateTime).format('YYYY-MM-DDTHH:mm:ssZ');
      } else if (formEndDate) {
        const endDateTime = formEndDate.endOf('day');
        formattedEndDate = toUTCMinus3(endDateTime).format('YYYY-MM-DDTHH:mm:ssZ');
      }
    } else {
      formattedStartDate = toUTCMinus3(formStartDate.startOf('day')).format('YYYY-MM-DDTHH:mm:ssZ');
      formattedEndDate = toUTCMinus3(formStartDate.endOf('day')).format('YYYY-MM-DDTHH:mm:ssZ');
    }

    if (!selectedEvent || selectedEvent.id === '') {
      const eventData = {
        companyId,
        title: formTitle || '',
        description: formDescription,
        local: formLocation,
        before_market: formTimeType as 'time' | 'before_market' | 'after_market' | 'full_day',
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        impact_id: formImpact,
        event_type_id: formEventType,
        shareholders: shareholdersToSave,
        is_created_by_admin: isAdmin,
      };

      createEventQuery.mutate(eventData);
    } else {
      const updateData = {
        id: selectedEvent.id,
        companyId,
        title: formTitle,
        description: formDescription,
        local: formLocation,
        before_market: formTimeType as 'time' | 'before_market' | 'after_market' | 'full_day',
        impact_id: formImpact,
        event_type_id: formEventType,
        shareholders: shareholdersToSave,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };

      updateEventQuery.mutate(updateData);
    }

    resetForm();
    setIsCreateOpen(false);
    setSelectedEvent(null);
    setIsEditMode(false);
  };

  const handleEditEvent = (event: EventSchema) => {
    setSelectedEvent(event);
    setIsEditMode(true);
    setIsCreateOpen(true);
  };

  const handleDeleteEvent = (event: EventSchema) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete || isDeletingEvent) return;

    setIsDeletingEvent(true);
    try {
      await deleteEventQuery.mutateAsync({
        id: eventToDelete.id,
        companyId,
      });
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      setIsCreateOpen(false);
      setSelectedEvent(null);
      setIsEditMode(false);
      resetForm();
    } catch (error) {
      // Error handling is done in the mutation
    } finally {
      setIsDeletingEvent(false);
    }
  };

  const addMarker = () => {
    if (!newMarkerName.trim()) return;

    const newMarker: Category = {
      id: String(Date.now()),
      name: newMarkerName,
      color: newMarkerColor,
    };

    setCategories((prev: Category[]) => prev.concat(newMarker));
    setIsMarkerModalOpen(false);
    setNewMarkerName('');
    setNewMarkerColor('#005462');
  };

  const openEditMarkerModal = (marker: Category) => {
    setEditingMarker(marker);
    setEditMarkerName(marker.name);
    setEditMarkerColor(marker.color);
    setIsEditMarkerModalOpen(true);
  };

  const updateMarker = () => {
    if (!editingMarker || !editMarkerName.trim()) return;

    setCategories((prev: Category[]) =>
      prev.map((cat) =>
        cat.id === editingMarker.id
          ? { ...cat, name: editMarkerName.trim(), color: editMarkerColor }
          : cat,
      ),
    );

    setIsEditMarkerModalOpen(false);
    setEditingMarker(null);
  };

  const deleteMarker = (markerId: string) => {
    setCategories((prev: Category[]) => prev.filter((cat) => cat.id !== markerId));
    setSelectedCategories((prev: string[]) => prev.filter((id) => id !== markerId));
  };

  const handlePageChange = (page: number, size?: number) => {
    setShareholderFiltersParams((prev) => ({
      ...prev,
      page,
      size: size ?? prev.size,
    }));
  };

  const handleShareholderSelection = (selectedRowKeys: React.Key[]) => {
    setSelectedShareholders(selectedRowKeys.map((key) => String(key)));
  };

  const dateCellRenderer = useCallback(
    (date: Dayjs) => {
      const isSelected = date.isSame(activeDate, 'date');
      const isToday = date.isSame(dayjs(), 'date');
      const isCurrentMonth = date.month() === activeDate.month();
      const bg = isSelected ? '#005462' : 'transparent';
      const color = isSelected ? '#fff' : isToday ? '#005462' : 'inherit';
      const border = isToday && !isSelected ? '1px solid #005462' : '1px solid transparent';

      const dayEvents = events.filter((e) => {
        const eventDate = getEventDate(e);
        if (!eventDate) return false;
        return eventDate.isSame(date, 'day');
      });

      const dayIrmEvents = irmEventsAsAdminEvents.filter((e) => {
        const eventDate = getEventDate(e);
        if (!eventDate) return false;
        return eventDate.isSame(date, 'day');
      });

      const eventColors = Array.from(
        new Set(
          [
            ...dayEvents.map((e) => {
              const category = categories.find((c) => c.id === e.categoryId);
              return category?.color || '#005462';
            }),
            ...dayIrmEvents.map(() => '#52c41a'),
          ].filter(Boolean),
        ),
      ).slice(0, 3);

      const className = [
        'mini-calendar-date-cell',
        isSelected ? 'mini-calendar-date-selected' : '',
        isToday ? 'mini-calendar-date-today' : '',
        !isCurrentMonth ? 'mini-calendar-date-other-month' : '',
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <div
          className={className}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 32 : isTablet ? 32 : 34,
            height: isMobile ? 32 : isTablet ? 32 : 34,
            margin: '1px auto',
            borderRadius: 999,
            background: bg,
            color,
            border,
            opacity: isCurrentMonth ? 1 : 0.3,
            position: 'relative',
          }}
        >
          <span className="mini-calendar-day-number">{date.date()}</span>
          {eventColors.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '2px',
                marginTop: '2px',
                justifyContent: 'center',
              }}
            >
              {eventColors.map((eventColor) => (
                <div
                  key={`${date.format('YYYY-MM-DD')}-${eventColor}`}
                  style={{
                    width: isMobile ? '3px' : '4px',
                    height: isMobile ? '3px' : '4px',
                    borderRadius: '50%',
                    backgroundColor: '#00d865',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
    [activeDate, events, irmEventsAsAdminEvents, categories, isMobile, isTablet, getEventDate],
  );

  const headerDays = view === 'day' ? 1 : 7;

  type CalendarOrAdmin = CalendarEventType | AdminEvent;

  const isAdminEventGuard = useCallback(
    (event: CalendarOrAdmin): event is AdminEvent => 'startHour' in event,
    [],
  );

  const isAllDayUnion = useCallback(
    (event: CalendarOrAdmin): boolean => {
      if (isAdminEventGuard(event)) {
        if (event.allDay) return true;
        if (typeof event.startHour === 'number' && event.startHour < 0) return true;
        const bm = event.before_market as
          | 'time'
          | 'before_market'
          | 'after_market'
          | 'full_day'
          | undefined;
        return bm === 'full_day' || bm === 'before_market' || bm === 'after_market';
      }
      const bm = (event as CalendarEventType).before_market as
        | 'time'
        | 'before_market'
        | 'after_market'
        | 'full_day'
        | undefined;
      return bm === 'full_day' || bm === 'before_market' || bm === 'after_market';
    },
    [isAdminEventGuard],
  );

  const getStartDate = useCallback((event: CalendarOrAdmin): Dayjs | null => {
    if ('start_date' in event) {
      const sd = event.start_date as string | Dayjs | undefined;
      if (!sd) return null;
      if (typeof sd === 'string') {
        return dayjs(sd.split('T')[0]).startOf('day');
      }
      return sd.startOf('day');
    }
    return null;
  }, []);

  const getEndDate = useCallback((event: CalendarOrAdmin, start: Dayjs | null): Dayjs | null => {
    if ('end_date' in event) {
      const ed = event.end_date as string | Dayjs | undefined;
      if (!ed) return start;
      if (typeof ed === 'string') {
        return dayjs(ed.split('T')[0]).startOf('day');
      }
      return ed.startOf('day');
    }
    return start;
  }, []);

  const toCalendar = (
    event: CalendarOrAdmin,
    localEvents: CalendarEventType[],
    remoteCalendarEvents: CalendarEventType[],
    defaultCompanyId: string,
  ): CalendarEventType => {
    if (!isAdminEventGuard(event)) return event as CalendarEventType;
    const found = [...localEvents, ...remoteCalendarEvents].find((x) => x.id === event.id);
    if (found) return found;

    const start = getStartDate(event) ?? dayjs();
    const end = getEndDate(event, start) ?? undefined;
    const impact = event.impact as CalendarEventType['impact'] | undefined;
    const eventType = event.event_type as CalendarEventType['event_type'] | undefined;

    return {
      id: event.id,
      title: event.title,
      description: event.description || '',
      start_date: start,
      end_date: end,
      local: event.local || '',
      before_market: event.before_market as CalendarEventType['before_market'],
      company_id: defaultCompanyId,
      favorite: event.favorite || false,
      hide: event.hide || false,
      impact: impact || { id: 0, name: 'Evento' },
      event_type:
        eventType ||
        ({
          id: 0,
          name: 'Evento',
          impact_id: (impact && impact.id) || 0,
          company_id: defaultCompanyId,
        } as CalendarEventType['event_type']),
      origin: 'Agenda',
      user: undefined,
      shareholder: undefined,
    } as CalendarEventType;
  };

  const allDayContainerHeight = useMemo(() => {
    const currentStart = view === 'day' ? activeDate.startOf('day') : weekStart;
    const daysInView = view === 'day' ? 1 : 7;

    const counts = Array.from({ length: daysInView }).map((_, idx) => {
      const date = currentStart.add(idx, 'day').startOf('day');
      const dayAllDayEvents = filteredEvents.filter((e) => {
        if (!isAllDayUnion(e)) return false;
        const s = getStartDate(e);
        const en = getEndDate(e, s);
        if (!s || !en) return false;
        return date.isSameOrAfter(s, 'day') && date.isSameOrBefore(en, 'day');
      });
      return dayAllDayEvents.length;
    });

    const maxCount = Math.max(0, ...counts);
    const rowHeight = 28;
    const gap = 4;
    const minHeight = rowHeight + 8;
    return maxCount > 0 ? maxCount * rowHeight + Math.max(0, maxCount - 1) * gap + 8 : minHeight;
  }, [view, activeDate, weekStart, filteredEvents, getStartDate, getEndDate, isAllDayUnion]);

  const renderAllDayBars = () => {
    const currentStart = view === 'day' ? activeDate.startOf('day') : weekStart;
    const daysInView = view === 'day' ? 1 : 7;

    const columns = Array.from({ length: daysInView }).map((_, idx) => {
      const date = currentStart.add(idx, 'day').startOf('day');

      const dayEvents = filteredEvents
        .filter((e) => {
          if (!isAllDayUnion(e)) return false;
          const s = getStartDate(e);
          const en = getEndDate(e, s);
          if (!s || !en) return false;
          const eventOccursOnThisDay =
            date.isSameOrAfter(s, 'day') && date.isSameOrBefore(en, 'day');
          return eventOccursOnThisDay;
        })
        .map((e) => toCalendar(e, localEventsAsCalendarEvents, calendarEvents, companyId))
        .sort((a, b) => {
          const dateCompare = a.start_date.diff(b.start_date);
          return dateCompare !== 0 ? dateCompare : a.title.localeCompare(b.title);
        });

      return (
        <div
          key={`allday-col-${currentStart.add(idx, 'day').format('YYYY-MM-DD')}`}
          style={{ padding: '6px 8px', minHeight: '100%', minWidth: 0, overflow: 'hidden' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {dayEvents.map((evt) => {
              const color = evt.impact ? getEventColor(evt.impact).border : '#005462';
              return (
                <Tooltip
                  key={`allday-${evt.id}`}
                  title={
                    <div style={{ maxWidth: 360 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, color: '#fff' }}>
                        {evt.title}
                      </div>
                      {evt.description && (
                        <div style={{ fontSize: 11, color: '#ddd' }}>{evt.description}</div>
                      )}
                      {evt.local && (
                        <div style={{ fontSize: 11, color: '#ddd', marginTop: 4 }}>
                          📍 {evt.local}
                        </div>
                      )}
                    </div>
                  }
                  placement="top"
                >
                  <button
                    type="button"
                    className="month-event-card"
                    style={{
                      width: '100%',
                      height: 24,
                      background: isDarkMode ? '#08444E' : color,
                      color: '#fff',
                      border: isDarkMode ? `1px solid ${color}` : 'none',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: '24px',
                      cursor: 'pointer',
                      padding: '0 8px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => {
                      const eventSchema: EventSchema = {
                        id: evt.id,
                        company_id: evt.company_id,
                        favorite: evt.favorite,
                        hide: evt.hide,
                        start_date: evt.start_date.toISOString(),
                        end_date: evt.end_date?.toISOString(),
                        title: evt.title,
                        local: evt.local,
                        before_market: evt.before_market,
                        impact: evt.impact,
                        origin: evt.origin,
                        event_type: evt.event_type,
                        description: evt.description,
                        is_created_by_admin: evt.is_created_by_admin,
                        user: evt.user,
                        shareholder: evt.shareholder,
                      };
                      handleEditEvent(eventSchema);
                    }}
                  >
                    {evt.title}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      );
    });

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${daysInView}, 1fr)`,
          height: allDayContainerHeight,
        }}
      >
        {columns}
      </div>
    );
  };

  const monthMatrix = useMemo(() => {
    const start = activeDate.startOf('month');
    const startMonday = start.startOf('week').add(1, 'day');
    const days: Dayjs[] = [];
    for (let i = 0; i < 42; i += 1) days.push(startMonday.add(i, 'day'));
    return days;
  }, [activeDate]);

  const renderMonth = () => (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        overflow: 'visible',
        minWidth: isMobile ? 7 * 180 : undefined,
      }}
    >
      <div
        className="header-view-options"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div className="agenda-month-header-buttons">
          <Button
            onClick={() => {
              const today = dayjs();
              setActiveDate(today);
              setWeekStart(today.startOf('week').add(1, 'day'));
            }}
          >
            Hoje
          </Button>

          <Select
            value={view}
            onChange={(v) => setView(v as typeof view)}
            style={{ minWidth: 160 }}
            size={isMobile ? 'small' : 'middle'}
            options={[
              { label: 'Semana', value: 'week' },
              { label: 'Dia', value: 'day' },
              { label: 'Mês', value: 'month' },
            ]}
          />

          <div className="agenda-month-header-buttons-container">
            <div className="g-btns">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const prev = activeDate.subtract(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(prev);
                  setWeekStart(prev.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const next = activeDate.add(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(next);
                  setWeekStart(next.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
            </div>

            <Typography.Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
              {getTitleText()}
            </Typography.Text>
          </div>
        </div>
        <Space>
          <Input.Search
            placeholder="Pesquisar eventos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: isMobile ? 150 : 200 }}
            allowClear
          />

          <Button
            type="default"
            onClick={() => setIsCreateOpen(true)}
            style={{
              background: isDarkMode ? '#282828' : '#fff',
              borderColor: isDarkMode ? '#d9d9d9' : '#d9d9d9',
              color: isDarkMode ? '#e0e0e0' : '#000',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget;
              target.style.background = isDarkMode ? '#333333' : '#f5f5f5';
              target.style.color = isDarkMode ? '#e0e0e0' : '#000';
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget;
              target.style.background = isDarkMode ? '#282828' : '#fff';
              target.style.color = isDarkMode ? '#e0e0e0' : '#000';
            }}
            icon={<PlusOutlined />}
          >
            Adicionar evento
          </Button>
        </Space>
      </div>

      <div
        className="agenda-month-container"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          background: BORDER,
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${BORDER}`,
        }}
      >
        {DAYS.map((day) => (
          <div
            key={day}
            style={{
              fontWeight: 600,
              padding: '10px 8px',
              color: isDarkMode ? '#e0e0e0' : 'inherit',
            }}
          >
            {day}
          </div>
        ))}

        {monthMatrix.map((date) => {
          const dateKey = date.format('YYYY-MM-DD');
          const isToday = date.isSame(dayjs(), 'day');
          const isCurrentMonth = date.isSame(activeDate, 'month');

          const dayEvents = filteredEvents.filter((e) => {
            const eventDate = getEventDate(e);
            if (!eventDate) return false;

            const eventStartDate = eventDate.startOf('day');
            const eventEndDate = e.end_date ? dayjs(e.end_date).startOf('day') : eventStartDate;
            const targetDate = date.startOf('day');

            return (
              targetDate.isSameOrAfter(eventStartDate) && targetDate.isSameOrBefore(eventEndDate)
            );
          });

          return (
            <div
              key={dateKey}
              style={{
                minHeight: isMobile ? 80 : 100,
                minWidth: 0,
                padding: isMobile ? '6px' : '10px',
                background: isDarkMode
                  ? isCurrentMonth
                    ? '#08444E'
                    : '#1a1a1a'
                  : isCurrentMonth
                  ? '#fff'
                  : '#fafafa',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: isToday ? '1px solid #eeeeee' : '1px solid transparent',
                boxShadow: isToday ? '0 0 0 1px rgba(0, 84, 98, 0.1)' : 'none',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isToday) {
                  e.currentTarget.style.background = isDarkMode ? '#333' : '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!isToday) {
                  e.currentTarget.style.background = isDarkMode
                    ? isCurrentMonth
                      ? '#08444E'
                      : '#1a1a1a'
                    : isCurrentMonth
                    ? '#fff'
                    : '#fafafa';
                }
              }}
              onClick={() => {
                setActiveDate(date);
                setView('day');
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveDate(date);
                  setView('day');
                }
              }}
            >
              {/* Número do dia */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: isToday ? 32 : 'auto',
                    height: isToday ? 32 : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: isToday ? '50%' : 0,
                    background: isToday ? '#005462' : 'transparent',
                    fontSize: isMobile ? 13 : isToday ? 16 : 14,
                    fontWeight: isToday ? 700 : isCurrentMonth ? 600 : 400,
                    color: isToday
                      ? '#fff'
                      : isCurrentMonth
                      ? isDarkMode
                        ? '#e0e0e0'
                        : '#333'
                      : isDarkMode
                      ? '#666'
                      : '#999',
                    transition: 'all 0.2s ease',
                    boxShadow: isToday ? '0 2px 8px rgba(0, 84, 98, 0.3)' : 'none',
                  }}
                >
                  {date.format('D')}
                </div>

                {/* Indicador de quantidade de eventos */}
                {dayEvents.length > 0 && (
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: isDarkMode ? '#999' : '#666',
                      background: isDarkMode ? '#0B5B68' : '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: 10,
                    }}
                  >
                    {dayEvents.length}
                  </div>
                )}
              </div>

              {/* Lista de eventos (máximo 2 visíveis) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                {dayEvents.slice(0, 2).map((e) => {
                  const eventColor = e.impact ? getEventColor(e.impact).border : '#005462';

                  return (
                    <Tooltip
                      key={e.id}
                      title={
                        <div style={{ maxWidth: 300 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, color: '#fff' }}>
                            {e.title}
                          </div>
                          {e.description && (
                            <div style={{ fontSize: 11, color: '#ddd', marginBottom: 4 }}>
                              {e.description}
                            </div>
                          )}
                          {e.local && (
                            <div style={{ fontSize: 11, color: '#ddd' }}>📍 {e.local}</div>
                          )}
                        </div>
                      }
                      placement="top"
                    >
                      <button
                        type="button"
                        className="month-event-card"
                        style={{
                          width: '100%',
                          height: 24,
                          background: isDarkMode ? '#08444E' : eventColor,
                          color: '#fff',
                          border: isDarkMode ? `1px solid ${eventColor}` : 'none',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          lineHeight: '24px',
                          cursor: 'pointer',
                          padding: '0 8px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textAlign: 'left',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          const calendarEvent =
                            calendarEventsWithCompanies.find((ce) => ce.id === e.id) ||
                            localEventsWithCompanies.find((ce) => ce.id === e.id);
                          if (calendarEvent) {
                            const eventSchema: EventSchema = {
                              id: calendarEvent.id,
                              company_id: calendarEvent.company_id,
                              favorite: calendarEvent.favorite,
                              hide: calendarEvent.hide,
                              start_date: calendarEvent.start_date.toISOString(),
                              end_date: calendarEvent.end_date?.toISOString(),
                              title: calendarEvent.title,
                              local: calendarEvent.local,
                              before_market: calendarEvent.before_market,
                              impact: calendarEvent.impact,
                              origin: calendarEvent.origin,
                              event_type: calendarEvent.event_type,
                              description: calendarEvent.description,
                              is_created_by_admin: calendarEvent.is_created_by_admin,
                              user: calendarEvent.user,
                              shareholder: calendarEvent.shareholder,
                            };
                            handleEditEvent(eventSchema);
                            return;
                          }

                          if (e.start_date) {
                            const fallbackImpactId = e.impact?.id ?? 1;
                            const eventSchema: EventSchema = {
                              id: e.id,
                              company_id: e.company_id || companyId,
                              favorite: e.favorite ?? false,
                              hide: e.hide ?? false,
                              start_date: dayjs(e.start_date).toISOString(),
                              end_date: e.end_date ? dayjs(e.end_date).toISOString() : undefined,
                              title: e.title,
                              local: e.local || '',
                              before_market: e.before_market,
                              impact: e.impact || { id: 1, name: 'Alto' },
                              origin: e.origin || 'Agenda',
                              event_type: e.event_type
                                ? {
                                    id: e.event_type.id,
                                    name: e.event_type.name,
                                    impact_id: fallbackImpactId,
                                    company_id: companyId,
                                  }
                                : {
                                    id: 1,
                                    name: 'Evento',
                                    impact_id: fallbackImpactId,
                                    company_id: companyId,
                                  },
                              description: e.description || '',
                              is_created_by_admin: e.is_created_by_admin,
                              user: e.user,
                              shareholder: e.shareholder,
                            };
                            handleEditEvent(eventSchema);
                          }
                        }}
                      >
                        {e.title}
                      </button>
                    </Tooltip>
                  );
                })}

                {/* Indicador de mais eventos */}
                {dayEvents.length > 2 && (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: isDarkMode ? '#888' : '#666',
                      padding: '4px 6px',
                      textAlign: 'center',
                      background: isDarkMode ? '#0B5B68' : '#f5f5f5',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setActiveDate(date);
                      setView('day');
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        ev.stopPropagation();
                        setActiveDate(date);
                        setView('day');
                      }
                    }}
                  >
                    +{dayEvents.length - 2} evento{dayEvents.length - 2 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // const confirmDeleteEvent = useCallback(() => {
  //   if (!eventToDelete) return;
  //   if (eventToDelete.company_id === 'local-agenda') {
  //     setEvents((prev) => prev.filter((e) => e.id !== eventToDelete.id));
  //   } else {
  //     deleteEventMutation.mutate({
  //       id: eventToDelete.id,
  //       companyId,
  //     });
  //   }
  //   setIsDeleteModalOpen(false);
  //   setEventToDelete(null);
  // }, [eventToDelete, deleteEventMutation, companyId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderWeekDayOld = () => (
    <div className="agenda-week-day-container">
      <div
        className="header-view-options"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div className="agenda-month-header-buttons">
          <Button
            onClick={() => {
              const today = dayjs();
              setActiveDate(today);
              setWeekStart(today.startOf('week').add(1, 'day'));
            }}
          >
            Hoje
          </Button>
          <div className="agenda-month-header-buttons-container">
            <div className="g-btns">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const prev = activeDate.subtract(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(prev);
                  setWeekStart(prev.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const next = activeDate.add(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(next);
                  setWeekStart(next.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
            </div>

            <Typography.Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
              {getTitleText()}
            </Typography.Text>
          </div>
        </div>
        <Select
          value={view}
          onChange={(v) => setView(v as typeof view)}
          style={{ minWidth: 160 }}
          size={isMobile ? 'small' : 'middle'}
          options={[
            { label: 'Semana', value: 'week' },
            { label: 'Dia', value: 'day' },
            { label: 'Mês', value: 'month' },
          ]}
        />
      </div>

      <div
        className="agenda-month-container"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          background: BORDER,
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${BORDER}`,
        }}
      >
        {DAYS.map((day) => (
          <div
            key={day}
            style={{
              fontWeight: 600,
              padding: '10px 8px',
              color: isDarkMode ? '#e0e0e0' : 'inherit',
            }}
          >
            {day}
          </div>
        ))}

        {monthMatrix.map((date) => {
          const dateKey = date.format('YYYY-MM-DD');
          const isToday = date.isSame(dayjs(), 'day');
          const isCurrentMonth = date.month() === activeDate.month();

          const dayEvents = filteredEvents.filter((e) => {
            const eventDate = getEventDate(e);
            if (!eventDate) return false;

            const eventStartDate = eventDate.startOf('day');
            const eventEndDate = e.end_date ? dayjs(e.end_date).startOf('day') : eventStartDate;
            const targetDate = date.startOf('day');

            return (
              targetDate.isSameOrAfter(eventStartDate) && targetDate.isSameOrBefore(eventEndDate)
            );
          });

          return (
            <div
              key={dateKey}
              style={{
                minHeight: isMobile ? 80 : 100,
                minWidth: 0,
                padding: isMobile ? '6px' : '10px',
                background: isDarkMode
                  ? isCurrentMonth
                    ? '#08444E'
                    : '#1a1a1a'
                  : isCurrentMonth
                  ? '#fff'
                  : '#fafafa',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: isToday ? '1px solid #eeeeee' : '1px solid transparent',
                boxShadow: isToday ? '0 0 0 1px rgba(0, 84, 98, 0.1)' : 'none',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isToday) {
                  e.currentTarget.style.background = isDarkMode ? '#333' : '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!isToday) {
                  e.currentTarget.style.background = isDarkMode
                    ? isCurrentMonth
                      ? '#08444E'
                      : '#1a1a1a'
                    : isCurrentMonth
                    ? '#fff'
                    : '#fafafa';
                }
              }}
              onClick={() => {
                setActiveDate(date);
                setView('day');
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveDate(date);
                  setView('day');
                }
              }}
            >
              {/* Número do dia */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: isToday ? 32 : 'auto',
                    height: isToday ? 32 : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: isToday ? '50%' : 0,
                    background: isToday ? '#005462' : 'transparent',
                    fontSize: isMobile ? 13 : isToday ? 16 : 14,
                    fontWeight: isToday ? 700 : isCurrentMonth ? 600 : 400,
                    color: isToday
                      ? '#fff'
                      : isCurrentMonth
                      ? isDarkMode
                        ? '#e0e0e0'
                        : '#333'
                      : isDarkMode
                      ? '#666'
                      : '#999',
                    transition: 'all 0.2s ease',
                    boxShadow: isToday ? '0 2px 8px rgba(0, 84, 98, 0.3)' : 'none',
                  }}
                >
                  {date.format('D')}
                </div>

                {/* Indicador de quantidade de eventos */}
                {dayEvents.length > 0 && (
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: isDarkMode ? '#999' : '#666',
                      background: isDarkMode ? '#0B5B68' : '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: 10,
                    }}
                  >
                    {dayEvents.length}
                  </div>
                )}
              </div>

              {/* Lista de eventos (máximo 2 visíveis) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                {dayEvents.slice(0, 2).map((e) => {
                  const eventColor = e.impact ? getEventColor(e.impact).border : '#005462';

                  return (
                    <Tooltip
                      key={e.id}
                      title={
                        <div style={{ maxWidth: 300 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, color: '#fff' }}>
                            {e.title}
                          </div>
                          {e.description && (
                            <div style={{ fontSize: 11, color: '#ddd', marginBottom: 4 }}>
                              {e.description}
                            </div>
                          )}
                          {e.local && (
                            <div style={{ fontSize: 11, color: '#ddd' }}>📍 {e.local}</div>
                          )}
                        </div>
                      }
                      placement="top"
                    >
                      <button
                        type="button"
                        className="month-event-card"
                        style={{
                          width: '100%',
                          height: 24,
                          background: isDarkMode ? '#08444E' : eventColor,
                          color: '#fff',
                          border: isDarkMode ? `1px solid ${eventColor}` : 'none',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          lineHeight: '24px',
                          cursor: 'pointer',
                          padding: '0 8px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textAlign: 'left',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          const calendarEvent =
                            calendarEventsWithCompanies.find((ce) => ce.id === e.id) ||
                            localEventsWithCompanies.find((ce) => ce.id === e.id);
                          if (calendarEvent) {
                            const eventSchema: EventSchema = {
                              id: calendarEvent.id,
                              company_id: calendarEvent.company_id,
                              favorite: calendarEvent.favorite,
                              hide: calendarEvent.hide,
                              start_date: calendarEvent.start_date.toISOString(),
                              end_date: calendarEvent.end_date?.toISOString(),
                              title: calendarEvent.title,
                              local: calendarEvent.local,
                              before_market: calendarEvent.before_market,
                              impact: calendarEvent.impact,
                              origin: calendarEvent.origin,
                              event_type: calendarEvent.event_type,
                              description: calendarEvent.description,
                              is_created_by_admin: calendarEvent.is_created_by_admin,
                              user: calendarEvent.user,
                              shareholder: calendarEvent.shareholder,
                            };
                            handleEditEvent(eventSchema);
                            return;
                          }

                          if (e.start_date) {
                            const fallbackImpactId = e.impact?.id ?? 1;
                            const eventSchema: EventSchema = {
                              id: e.id,
                              company_id: e.company_id || companyId,
                              favorite: e.favorite ?? false,
                              hide: e.hide ?? false,
                              start_date: dayjs(e.start_date).toISOString(),
                              end_date: e.end_date ? dayjs(e.end_date).toISOString() : undefined,
                              title: e.title,
                              local: e.local || '',
                              before_market: e.before_market,
                              impact: e.impact || { id: 1, name: 'Alto' },
                              origin: e.origin || 'Agenda',
                              event_type: e.event_type
                                ? {
                                    id: e.event_type.id,
                                    name: e.event_type.name,
                                    impact_id: fallbackImpactId,
                                    company_id: companyId,
                                  }
                                : {
                                    id: 1,
                                    name: 'Evento',
                                    impact_id: fallbackImpactId,
                                    company_id: companyId,
                                  },
                              description: e.description || '',
                              is_created_by_admin: e.is_created_by_admin,
                              user: e.user,
                              shareholder: e.shareholder,
                            };
                            handleEditEvent(eventSchema);
                          }
                        }}
                      >
                        {e.title}
                      </button>
                    </Tooltip>
                  );
                })}
                {/* Indicador de mais eventos */}
                {dayEvents.length > 2 && (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: isDarkMode ? '#888' : '#666',
                      padding: '4px 6px',
                      textAlign: 'center',
                      background: isDarkMode ? '#0B5B68' : '#f5f5f5',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveDate(date);
                      setView('day');
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        setActiveDate(date);
                        setView('day');
                      }
                    }}
                  >
                    +{dayEvents.length - 2} evento{dayEvents.length - 2 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderWeekDay = () => (
    <div
      className="agenda-week-day-container"
      style={{
        minWidth: isMobile ? 60 + headerDays * 220 : undefined,
      }}
    >
      <div
        className="header-view-options"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div className="agenda-month-header-buttons">
          <Button
            onClick={() => {
              const today = dayjs();
              setActiveDate(today);
              setWeekStart(today.startOf('week').add(1, 'day'));
            }}
          >
            Hoje
          </Button>

          <Select
            value={view}
            onChange={(v) => setView(v as typeof view)}
            style={{ minWidth: 160 }}
            size={isMobile ? 'small' : 'middle'}
            options={[
              { label: 'Semana', value: 'week' },
              { label: 'Dia', value: 'day' },
              { label: 'Mês', value: 'month' },
            ]}
          />

          <div className="agenda-month-header-buttons-container">
            <div className="g-btns">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const prev = activeDate.subtract(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(prev);
                  setWeekStart(prev.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={() => {
                  const delta =
                    view === 'month'
                      ? { unit: 'month', step: 1 }
                      : view === 'week'
                      ? { unit: 'week', step: 1 }
                      : { unit: 'day', step: 1 };
                  const next = activeDate.add(delta.step, delta.unit as dayjs.ManipulateType);
                  setActiveDate(next);
                  setWeekStart(next.startOf('week').add(1, 'day'));
                }}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
            </div>

            <Typography.Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
              {getTitleText()}
            </Typography.Text>
          </div>
        </div>
        <Space>
          <Input.Search
            placeholder="Pesquisar eventos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: isMobile ? 150 : 200 }}
            allowClear
          />

          <Button
            type="default"
            onClick={() => setIsCreateOpen(true)}
            style={{
              background: isDarkMode ? '#282828' : '#fff',
              borderColor: isDarkMode ? '#d9d9d9' : '#d9d9d9',
              color: isDarkMode ? '#e0e0e0' : '#000',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget;
              target.style.background = isDarkMode ? '#333333' : '#f5f5f5';
              target.style.color = isDarkMode ? '#e0e0e0' : '#000';
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget;
              target.style.background = isDarkMode ? '#282828' : '#fff';
              target.style.color = isDarkMode ? '#e0e0e0' : '#000';
            }}
            icon={<PlusOutlined />}
          >
            Adicionar evento
          </Button>
        </Space>
      </div>

      <div
        className="agenda-week-day-content"
        style={{
          overflow: 'hidden',
        }}
      >
        <div
          className="agenda-week-day-header"
          style={
            {
              display: 'grid',
              gridTemplateColumns: `60px repeat(${headerDays}, minmax(80px, 1fr))`,
              minWidth: '600px',
              '--agenda-header-template': `60px repeat(${headerDays}, minmax(80px, 1fr))`,
            } as CSSProperties
          }
        >
          <div
            style={{
              borderRight: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              borderLeft: `1px solid ${BORDER}`,
              background: HEADER_BG,
            }}
          />
          {Array.from({ length: headerDays }).map((_, idx) => {
            const date = view === 'day' ? activeDate : weekStart.add(idx, 'day');
            const dateKey = date.format('YYYY-MM-DD');
            const dayOfWeek = date.day();
            const isTodayHeader = date.isSame(dayjs(), 'day');
            return (
              <div
                key={`hdr-${dateKey}`}
                style={{
                  padding: 4,
                  borderRight: `1px solid ${BORDER}`,
                  borderBottom: `1px solid ${BORDER}`,
                  background: HEADER_BG,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography.Text
                  strong
                  style={{
                    color: isTodayHeader ? '#005462' : 'inherit',
                    fontWeight: isTodayHeader ? 700 : 'inherit',
                  }}
                >
                  {view === 'week' ? DAYS_SHORT[dayOfWeek] : DAYS[dayOfWeek]}
                </Typography.Text>
                <div
                  style={{
                    fontSize: 11,
                    color: isTodayHeader ? '#005462' : 'var(--text-color-secondary)',
                    fontWeight: isTodayHeader ? 600 : 'normal',
                  }}
                >
                  {date.format('DD/MM')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          maxHeight: isMobile ? '60vh' : '70vh',
          overflowY: 'auto',
          overflowX: 'visible',
          scrollbarGutter: 'stable',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `60px repeat(${headerDays}, 1fr)`,
            gridTemplateRows: `${allDayContainerHeight}px auto`,
            width: '100%',
          }}
        >
          <div
            style={{
              borderRight: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              borderLeft: `1px solid ${BORDER}`,
              background: HEADER_BG,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          />

          <div
            style={{
              gridColumn: `2 / span ${headerDays}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              borderBottom: `1px solid ${BORDER}`,
              background: '#fff',
              height: allDayContainerHeight,
              overflow: 'hidden',
            }}
          >
            {renderAllDayBars()}
          </div>

          <div
            style={{
              borderRight: `1px solid ${BORDER}`,
              borderLeft: `1px solid ${BORDER}`,
              background: HEADER_BG,
              position: 'relative',
            }}
          >
            {HOURS.map((h) => (
              <div
                key={`hours-${h}`}
                style={{
                  height: HOUR_ROW_HEIGHT,
                  borderBottom: `1px solid ${BORDER}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  paddingRight: 4,
                  fontSize: 11,
                  color: 'var(--text-color-secondary)',
                }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {Array.from({ length: headerDays }).map((_, idx) => {
            const date = view === 'day' ? activeDate : weekStart.add(idx, 'day');
            const dateKey = date.format('YYYY-MM-DD');
            const isTodayColumn = date.isSame(dayjs(), 'day');

            const dayEvents = filteredEvents.filter((e) => {
              const eventDate = getEventDate(e);
              if (!eventDate) return false;

              if (e.allDay) return false;
              if (e.before_market === 'full_day') return false;
              if (e.before_market === 'before_market') return false;
              if (e.before_market === 'after_market') return false;
              if ('startHour' in e && e.startHour < 0) return false;

              const eventStartDate = eventDate.startOf('day');
              const eventEndDate = e.end_date
                ? (typeof e.end_date === 'string' ? dayjs(e.end_date) : e.end_date).startOf('day')
                : eventStartDate;
              const targetDate = date.startOf('day');

              return (
                targetDate.isSameOrAfter(eventStartDate) && targetDate.isSameOrBefore(eventEndDate)
              );
            });
            const now = dayjs();
            const showNow = now.isSame(date, 'day');
            const topNow = `${Math.max(
              0,
              Math.min(nowMinutes * (HOUR_ROW_HEIGHT / 60), HOURS.length * HOUR_ROW_HEIGHT),
            )}px`;
            return (
              <div
                key={`col-${dateKey}`}
                style={{
                  borderRight: `1px solid ${BORDER}`,
                  background: isTodayColumn ? 'rgba(0, 84, 98, 0.02)' : '#fff',
                  position: 'relative',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <DayColumn dayIndex={idx}>
                    {HOURS.map((hour) => (
                      <div
                        key={`cell-${dateKey}-${hour}`}
                        style={{
                          position: 'absolute',
                          top: (hour - START_HOUR) * HOUR_ROW_HEIGHT,
                          left: 0,
                          right: 0,
                          height: HOUR_ROW_HEIGHT,
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          zIndex: 0,
                        }}
                        className="calendar-time-cell"
                        onClick={() => handleCellClick(date, hour)}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          target.style.backgroundColor = 'rgba(0, 84, 98, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          target.style.backgroundColor = 'transparent';
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCellClick(date, hour);
                          }
                        }}
                        aria-label={`Criar evento em ${date.format('DD/MM/YYYY')} às ${hour}:00`}
                      />
                    ))}

                    {(() => {
                      const eventLayout = calculateEventLayout(dayEvents);
                      return eventLayout.map((eventWithLayout) => (
                        <DraggableEvent
                          key={eventWithLayout.id}
                          evt={eventWithLayout}
                          layout={{
                            left: eventWithLayout.left,
                            width: eventWithLayout.width,
                            zIndex: eventWithLayout.zIndex,
                          }}
                          onEventClick={(adminEvent: AdminEvent) => {
                            const eventSchema: EventSchema = {
                              id: adminEvent.id,
                              company_id: companyId,
                              favorite: adminEvent.favorite || false,
                              hide: adminEvent.hide || false,
                              start_date:
                                adminEvent.start_date || dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
                              end_date: adminEvent.end_date,
                              title: adminEvent.title,
                              local: adminEvent.local || '',
                              before_market:
                                (adminEvent.before_market as EventSchema['before_market']) ||
                                'time',
                              impact: adminEvent.impact || { id: 1, name: 'Evento' },
                              origin: adminEvent.origin || 'Agenda',
                              event_type: adminEvent.event_type
                                ? {
                                    id: adminEvent.event_type.id,
                                    name: adminEvent.event_type.name,
                                    impact_id: adminEvent.impact?.id || 1,
                                    company_id: companyId,
                                  }
                                : {
                                    id: 1,
                                    name: 'Evento',
                                    impact_id: adminEvent.impact?.id || 1,
                                    company_id: companyId,
                                  },
                              description: adminEvent.description || '',
                              is_created_by_admin: (adminEvent as { is_created_by_admin?: boolean })
                                .is_created_by_admin,
                              user: adminEvent.user
                                ? {
                                    name: adminEvent.user.name,
                                    image_url: adminEvent.user.image_url || '',
                                  }
                                : { name: 'Usuário', image_url: '' },
                              shareholder: adminEvent.shareholder,
                            };

                            handleEditEvent(eventSchema);
                          }}
                        />
                      ));
                    })()}
                    {showNow && (
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          height: 2,
                          background: '#ff4d4f',
                          top: topNow,
                          zIndex: 1000,
                          pointerEvents: 'none',
                          boxShadow: '0 0 4px rgba(255, 77, 79, 0.5)',
                        }}
                      />
                    )}
                  </DayColumn>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const SidebarHeader = (
    <div
      className="sidebar-header-main"
      style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}
    >
      <Space className="calendar-search-alert">
        <Dropdown
          open={isUpdatesModalOpen}
          onOpenChange={(open) => {
            setIsUpdatesModalOpen(open);
            if (open) {
              setHasUnreadNotifications(false);
            }
          }}
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => UpdatesDropdownContent}
        >
          <div style={{ position: 'relative' }}>
            <Button
              type="text"
              icon={<BellOutlined />}
              style={{
                fontSize: '16px',
                color: '#005462',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
              }}
            />
            {hasUnreadNotifications && (
              <div
                style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#ff4d4f',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  zIndex: 1,
                }}
              />
            )}
          </div>
        </Dropdown>
      </Space>
    </div>
  );

  const Sidebar = (
    <div className="agenda-sidebar-container">
      <div style={{ width: '100%' }}>
        <div className="mini-calendar-container">
          <Calendar
            className="mini-calendar"
            fullscreen={false}
            value={activeDate}
            locale={{
              lang: {
                locale: 'pt-br',
                today: 'Hoje',
                now: 'Agora',
                backToToday: 'Voltar para hoje',
                ok: 'OK',
                clear: 'Limpar',
                month: 'Mês',
                year: 'Ano',
                timeSelect: 'Selecionar hora',
                dateSelect: 'Selecionar data',
                monthSelect: 'Escolher mês',
                yearSelect: 'Escolher ano',
                decadeSelect: 'Escolher década',
                yearFormat: 'YYYY',
                dateFormat: 'DD/MM/YYYY',
                dayFormat: 'D',
                dateTimeFormat: 'DD/MM/YYYY HH:mm:ss',
                monthFormat: 'MMMM',
                monthBeforeYear: true,
                previousMonth: 'Mês anterior',
                nextMonth: 'Próximo mês',
                previousYear: 'Ano anterior',
                nextYear: 'Próximo ano',
                previousDecade: 'Década anterior',
                nextDecade: 'Próxima década',
                previousCentury: 'Século anterior',
                nextCentury: 'Próximo século',
                shortWeekDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
                shortMonths: [
                  'Jan',
                  'Fev',
                  'Mar',
                  'Abr',
                  'Mai',
                  'Jun',
                  'Jul',
                  'Ago',
                  'Set',
                  'Out',
                  'Nov',
                  'Dez',
                ],
                placeholder: 'Selecionar data',
              },
              timePickerLocale: {
                placeholder: 'Selecionar hora',
              },
              dateFormat: 'DD/MM/YYYY',
              dateTimeFormat: 'DD/MM/YYYY HH:mm:ss',
              weekFormat: 'wo',
              monthFormat: 'MMMM',
            }}
            dateFullCellRender={dateCellRenderer}
            onSelect={(d) => {
              if (d && d.isValid()) {
                // Garantir que a data selecionada use o locale pt-BR
                const dateWithLocale = d.locale('pt-br');
                setActiveDate(dateWithLocale);
                setWeekStart(dateWithLocale.startOf('week').add(1, 'day'));
                // Removido setSelectedDate para não filtrar por data
              }
            }}
            headerRender={({ value, onChange }) =>
              renderMiniHeader({
                value: value as Dayjs,
                onChange: (d: Dayjs) => {
                  setActiveDate(d);
                  onChange(d);
                },
              })
            }
            style={{
              padding: 0,
              width: '100%',
              fontSize: isMobile ? '12px' : '14px',
            }}
          />
        </div>
      </div>

      <div>
        <div className="marcadores-main">
          <Typography.Text strong>Minhas agendas</Typography.Text>
        </div>

        {impactEventsQuery.data?.data && (
          <div className="impact-items-container">
            {impactEventsQuery.data.data
              .sort((a, b) => {
                // Ordem personalizada: Acionista, Companhia, Setor/Mercado, Regulatório CVM/B3
                return getOrderIndex(a.name) - getOrderIndex(b.name);
              })
              .map((impact) => {
                const agendaColor = getAgendaColor(impact.name);
                return (
                  <div key={impact.id} className="impact-item">
                    <Checkbox
                      className="impact-checkbox"
                      checked={selectedImpacts.includes(impact.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedImpacts((prev) => [...prev, impact.id]);
                        } else {
                          setSelectedImpacts((prev) => prev.filter((id) => id !== impact.id));
                        }
                      }}
                      style={
                        {
                          marginRight: '8px',
                          '--checkbox-color': agendaColor,
                        } as React.CSSProperties
                      }
                    />
                    <Typography.Text className="impact-label" style={{ fontWeight: 500 }}>
                      {impact.name}
                    </Typography.Text>
                  </div>
                );
              })}
          </div>
        )}

        <div className="marcadores-list">
          {categories.map((c) => {
            const menuItems = [
              {
                key: 'edit',
                label: 'Editar',
                icon: <EditOutlined />,
                onClick: () => openEditMarkerModal(c),
              },
              {
                key: 'delete',
                label: 'Excluir',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => deleteMarker(c.id),
              },
            ];

            return (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <Checkbox
                  checked={selectedCategories.includes(c.id)}
                  onChange={(e) => {
                    if (e.target.checked)
                      setSelectedCategories((prev) => Array.from(new Set([...prev, c.id])));
                    else setSelectedCategories((prev) => prev.filter((id) => id !== c.id));
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, background: c.color, borderRadius: 999 }} />
                    {c.name}
                  </span>
                </Checkbox>
                <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreOutlined style={{ fontSize: '16px' }} />}
                    style={{
                      padding: '4px 8px',
                      height: 'auto',
                      backgroundColor: 'transparent',
                      color: '#666',
                      minWidth: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.color = '#333';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#666';
                    }}
                  />
                </Dropdown>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <Content style={{ backgroundColor: 'var(--background)', minHeight: '100vh' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {SidebarHeader}

          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 16,
              width: '100%',
            }}
          >
            <aside
              style={{
                background: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 10,
                height: 'fit-content',
                position: isMobile ? 'static' : 'sticky',
                top: isMobile ? 'auto' : 71,
                width: sidebarWidth,
                flexShrink: 0,
                maxWidth: sidebarWidth,
                minWidth: sidebarWidth,
              }}
            >
              {Sidebar}
            </aside>
            <main
              className="agenda-main-content"
              style={{
                minHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                flex: 1,
                minWidth: 0,
                maxWidth: agendaContentMaxWidth,
                overflow: 'hidden', // Previne overflow
              }}
            >
              <div
                className="agenda-calendar-container"
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  // Enable horizontal scroll on mobile to avoid squeezing the calendar
                  overflowX: isMobile ? 'auto' : 'visible',
                  WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
                }}
              >
                {view === 'month' ? renderMonth() : renderWeekDay()}
              </div>

              <div className={styles.listEventsContainer}>
                <div className={styles.listEvents}>
                  {/* Lista de Próximos Eventos */}
                  <div
                    style={{
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '0px',
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}
                    >
                      Próximos eventos
                    </Typography.Text>
                  </div>

                  <div style={{ padding: '16px', overflow: 'auto' }}>
                    {allCalendarEvents && allCalendarEvents.length > 0 ? (
                      allCalendarEvents
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map((event: CalendarEventType) => (
                          <div
                            key={event.id}
                            className={styles.eventCard}
                            style={{
                              marginBottom: '12px',
                              padding: '16px',
                              backgroundColor: 'var(--background-secondary)',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${getEventColor(event.impact).border}`,
                              cursor: 'pointer',
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              // Converter CalendarEventType para EventSchema e abrir drawer de edição
                              const eventSchema: EventSchema = {
                                id: event.id,
                                company_id: event.company_id,
                                favorite: event.favorite,
                                hide: event.hide,
                                start_date: event.start_date.toISOString(),
                                end_date: event.end_date?.toISOString(),
                                title: event.title,
                                local: event.local,
                                before_market: event.before_market,
                                impact: event.impact,
                                origin: event.origin,
                                event_type: event.event_type,
                                description: event.description,
                                is_created_by_admin: event.is_created_by_admin,
                                user: event.user,
                                shareholder: event.shareholder,
                              };
                              handleEditEvent(eventSchema);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const eventSchema: EventSchema = {
                                  id: event.id,
                                  company_id: event.company_id,
                                  favorite: event.favorite,
                                  hide: event.hide,
                                  start_date: event.start_date.toISOString(),
                                  end_date: event.end_date?.toISOString(),
                                  title: event.title,
                                  local: event.local,
                                  before_market: event.before_market,
                                  impact: event.impact,
                                  origin: event.origin,
                                  event_type: event.event_type,
                                  description: event.description,
                                  is_created_by_admin: event.is_created_by_admin,
                                  user: event.user,
                                  shareholder: event.shareholder,
                                };
                                handleEditEvent(eventSchema);
                              }
                            }}
                          >
                            <div className={styles.eventCardContent}>
                              <div className={styles.eventDate}>
                                <Typography.Text strong>
                                  {event.start_date.format('DD')}
                                </Typography.Text>
                                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                  {event.start_date.locale('pt-br').format('MMM')}
                                </Typography.Text>
                              </div>
                              <div className={styles.eventDetails}>
                                <div className={styles.eventHeader}>
                                  <div className={styles.eventTitle}>
                                    <Space align="center">
                                      <Typography.Text strong>{event.title}</Typography.Text>
                                      <Tag color={getEventTagColor(event.impact)}>
                                        {event.event_type.name}
                                      </Tag>
                                    </Space>
                                    {event.local && event.local.trim() !== '' && (
                                      <div className={styles.eventLocation}>
                                        <EnvironmentOutlined />
                                        <Typography.Text type="secondary">
                                          {event.local}
                                        </Typography.Text>
                                      </div>
                                    )}
                                    <Typography.Paragraph
                                      type="secondary"
                                      ellipsis={{ rows: 2 }}
                                      style={{ marginBottom: 0 }}
                                    >
                                      {event.description}
                                    </Typography.Paragraph>
                                    {event.user && event.user.name && (
                                      <Typography.Text
                                        type="secondary"
                                        style={{ fontSize: '12px', marginTop: '4px' }}
                                      >
                                        Criado por: {event.user.name}
                                      </Typography.Text>
                                    )}
                                  </div>
                                  <Space
                                    align="start"
                                    size="middle"
                                    className="agenda-icon-button-container edit-button-event-container "
                                  >
                                    <Tooltip title="Editar evento">
                                      <Button
                                        type="default"
                                        className="agenda-icon-button"
                                        icon={<EditOutlined />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const eventSchema: EventSchema = {
                                            id: event.id,
                                            company_id: event.company_id,
                                            favorite: event.favorite,
                                            hide: event.hide,
                                            start_date: event.start_date.toISOString(),
                                            end_date: event.end_date?.toISOString(),
                                            title: event.title,
                                            local: event.local,
                                            before_market: event.before_market,
                                            impact: event.impact,
                                            origin: event.origin,
                                            event_type: event.event_type,
                                            description: event.description,
                                            is_created_by_admin: event.is_created_by_admin,
                                            user: event.user,
                                            shareholder: event.shareholder,
                                          };
                                          handleEditEvent(eventSchema);
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Solicitar apoio">
                                      <Button
                                        type="default"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSupportEvent(event);
                                          setIsSupportDrawerOpen(true);
                                        }}
                                      >
                                        Solicitar apoio
                                      </Button>
                                    </Tooltip>
                                  </Space>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 200,
                          color: '#999',
                          fontSize: 14,
                        }}
                      >
                        Nenhum evento cadastrado
                      </div>
                    )}
                    {allCalendarEvents && allCalendarEvents.length > itemsPerPage && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '16px',
                          padding: '0 16px',
                        }}
                      >
                        <Pagination
                          current={currentPage}
                          total={allCalendarEvents.length}
                          pageSize={itemsPerPage}
                          onChange={setCurrentPage}
                          showSizeChanger={false}
                          showQuickJumper={false}
                          showTotal={(total, range) =>
                            `${range[0]}-${range[1]} de ${total} rotinas`
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>

        <EventDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedEventForDetails(null);
          }}
          event={selectedEventForDetails || undefined}
        />
        {supportEvent && (
          <SupportRequestDrawer
            isOpen={isSupportDrawerOpen}
            onClose={handleCloseSupportDrawer}
            event={{
              ...supportEvent,
              date: supportEvent.start_date.locale('pt-br').format('DD/MM/YYYY'),
            }}
          />
        )}

        <Drawer
          title={isEditMode ? 'Editar Evento' : 'Criar Evento'}
          open={isCreateOpen}
          onClose={() => {
            setIsCreateOpen(false);
            setSelectedEvent(null);
            setIsEditMode(false);
            resetForm();
            setValidationErrors({});
            resetPagination();
          }}
          width={isMobile ? '90%' : 600}
          destroyOnClose
          placement="right"
          style={{ marginTop: 0, paddingTop: 0 }}
          extra={
            <Space>
              {selectedEvent && (
                <Button
                  type="default"
                  danger
                  onClick={() => handleDeleteEvent(selectedEvent)}
                  style={{ height: 36, padding: '8px 16px', fontWeight: 500 }}
                  loading={deleteEventQuery.isLoading}
                  disabled={!!selectedEvent.is_created_by_admin}
                >
                  Excluir
                </Button>
              )}
              <Button
                type="default"
                onClick={() => {
                  setIsCreateOpen(false);
                  setSelectedEvent(null);
                  setIsEditMode(false);
                  resetForm();
                }}
                style={{ height: 36, padding: '8px 16px', fontWeight: 500 }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                disabled={
                  !formTitle.trim() ||
                  !formStartDate ||
                  !formImpact ||
                  !formEventType ||
                  (formTimeType === 'time' && !formStartTime)
                }
                onClick={handleSave}
              >
                Salvar
              </Button>
            </Space>
          }
        >
          <Tabs
            defaultActiveKey="geral"
            style={{ marginTop: 0, paddingTop: 0 }}
            items={[
              {
                key: 'geral',
                label: 'Geral',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ marginTop: 0 }}>
                      <Typography.Text strong>Agenda *</Typography.Text>
                      <Select
                        size="middle"
                        value={formImpact}
                        onChange={(value) => {
                          setFormImpact(value);
                          setFormEventType(undefined);
                          if (validationErrors.impact) {
                            setValidationErrors((prev) => ({ ...prev, impact: undefined }));
                          }
                          if (validationErrors.eventType) {
                            setValidationErrors((prev) => ({ ...prev, eventType: undefined }));
                          }
                        }}
                        style={{
                          marginTop: 4,
                          width: '100%',
                          borderRadius: '8px',
                        }}
                        placeholder="Minhas agendas"
                        status={validationErrors.impact ? 'error' : undefined}
                        loading={impactEventsQuery.isLoading}
                        suffixIcon={<CalendarOutlined />}
                      >
                        {impactEventsQuery.data?.data?.map((impact) => {
                          const agendaColor = getAgendaColor(impact.name);
                          return (
                            <Select.Option key={impact.id} value={impact.id}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: agendaColor,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                  }}
                                />
                                {impact.name}
                              </span>
                            </Select.Option>
                          );
                        })}
                      </Select>
                      {validationErrors.impact && (
                        <Typography.Text
                          type="danger"
                          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                        >
                          {validationErrors.impact}
                        </Typography.Text>
                      )}
                    </div>

                    <div style={{ marginTop: 0 }}>
                      <Typography.Text strong>Tipo de Evento *</Typography.Text>
                      <Select
                        size="middle"
                        value={formEventType}
                        onChange={(value) => {
                          setFormEventType(value);
                          if (validationErrors.eventType) {
                            setValidationErrors((prev) => ({ ...prev, eventType: undefined }));
                          }
                        }}
                        style={{
                          marginTop: 4,
                          width: '100%',
                          borderRadius: '8px',
                        }}
                        status={validationErrors.eventType ? 'error' : undefined}
                        placeholder={
                          getEventTypesForImpact.length > 0
                            ? 'Selecione um tipo'
                            : 'Selecione uma agenda primeiro'
                        }
                        disabled={getEventTypesForImpact.length === 0}
                        suffixIcon={<ExclamationCircleOutlined />}
                      >
                        {eventTypeOptions.map((eventType) => (
                          <Select.Option key={eventType.id} value={eventType.id}>
                            {eventType.name}
                          </Select.Option>
                        ))}
                      </Select>
                      {validationErrors.eventType && (
                        <Typography.Text
                          type="danger"
                          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                        >
                          {validationErrors.eventType}
                        </Typography.Text>
                      )}
                    </div>

                    <div>
                      <Typography.Text strong>Título *</Typography.Text>
                      <Input
                        size="middle"
                        placeholder="Descreva seu titulo"
                        value={formTitle}
                        onChange={(e) => {
                          setFormTitle(e.target.value);
                          if (validationErrors.title) {
                            setValidationErrors((prev) => ({ ...prev, title: undefined }));
                          }
                        }}
                        style={{
                          marginTop: 4,
                          width: '100%',
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                        }}
                        status={validationErrors.title ? 'error' : undefined}
                      />
                      {validationErrors.title && (
                        <Typography.Text
                          type="danger"
                          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                        >
                          {validationErrors.title}
                        </Typography.Text>
                      )}
                    </div>

                    <div>
                      <Typography.Text strong>Local</Typography.Text>
                      <Input
                        size="middle"
                        placeholder="Ex: Sala de reuniões, Online, etc.."
                        value={formLocation}
                        onChange={(e) => setFormLocation(e.target.value)}
                        style={{
                          marginTop: 4,
                          width: '100%',
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </div>

                    <div>
                      <Typography.Text strong>Descrição</Typography.Text>
                      <Input.TextArea
                        placeholder="Descreva seu evento"
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                        style={{
                          marginTop: 4,
                          width: '100%',
                          height: '100px',
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                        }}
                        rows={3}
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            padding: '12px 16px',
                            backgroundColor: isDarkMode ? '#282828' : '#f5f5f5',
                            border: isDarkMode ? '1px solid #d9d9d9' : '1px solid #d9d9d9',
                            borderRadius: '6px',
                            flex: 1,
                          }}
                        >
                          <Typography.Text
                            strong
                            style={{ fontSize: '14px', color: isDarkMode ? '#e0e0e0' : '#333' }}
                          >
                            Data de início
                          </Typography.Text>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <DatePicker
                              size="middle"
                              value={formStartDate}
                              onChange={(value) => {
                                setFormStartDate(value);
                                if (value) {
                                  setFormEndDate(value);
                                }
                                if (validationErrors.date) {
                                  setValidationErrors((prev) => ({ ...prev, date: undefined }));
                                }
                              }}
                              style={{
                                width: formTimeType === 'time' ? 'auto' : '100%',
                                flex: formTimeType === 'time' ? '1' : 'none',
                                fontSize: '14px',
                                height: '40px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                textAlign: 'left',
                                padding: '0',
                                margin: '0',
                                color: isDarkMode ? '#fff' : '#333',
                              }}
                              placeholder="Selecione uma data"
                              format="DD/MM/YYYY"
                              status={validationErrors.date ? 'error' : undefined}
                              suffixIcon={<CalendarOutlined />}
                              bordered={!isDarkMode}
                            />
                            {formTimeType === 'time' && (
                              <DatePicker
                                value={formStartTime}
                                onChange={(value) => {
                                  setFormStartTime(value);
                                  if (validationErrors.startTime) {
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      startTime: undefined,
                                    }));
                                  }
                                }}
                                style={{
                                  width: '80px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  fontSize: '14px',
                                  height: '40px',
                                  boxShadow: 'none',
                                }}
                                format="HH:mm"
                                picker="time"
                                status={validationErrors.startTime ? 'error' : undefined}
                                bordered
                              />
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            padding: '12px 16px',
                            backgroundColor: isDarkMode ? '#282828' : '#f5f5f5',
                            border: isDarkMode ? '1px solid #d9d9d9' : '1px solid #d9d9d9',
                            borderRadius: '6px',
                            flex: 1,
                          }}
                        >
                          <Typography.Text
                            strong
                            style={{ fontSize: '14px', color: isDarkMode ? '#e0e0e0' : '#333' }}
                          >
                            Data fim
                          </Typography.Text>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <DatePicker
                              size="middle"
                              value={formEndDate}
                              onChange={(value) => {
                                setFormEndDate(value);
                                if (!value) {
                                  setFormEndTime(null);
                                }

                                if (value && formStartDate) {
                                  if (formIsFullDay) {
                                    if (value.isBefore(formStartDate, 'day')) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        endDate:
                                          'Data de término deve ser posterior à data de início',
                                      }));
                                    } else {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        endDate: undefined,
                                      }));
                                    }
                                  } else if (
                                    formStartTime &&
                                    formEndTime &&
                                    formTimeType === 'time'
                                  ) {
                                    const startDateTime = dayjs(
                                      `${formStartDate.format('YYYY-MM-DD')} ${formStartTime.format(
                                        'HH:mm',
                                      )}`,
                                    );
                                    const endDateTime = dayjs(
                                      `${value.format('YYYY-MM-DD')} ${formEndTime.format(
                                        'HH:mm',
                                      )}`,
                                    );

                                    if (endDateTime.isSameOrBefore(startDateTime)) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        endTime:
                                          'Data e hora de término devem ser posteriores à data e hora de início',
                                      }));
                                    } else {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        endTime: undefined,
                                      }));
                                    }
                                  }
                                }
                              }}
                              style={{
                                width: formTimeType === 'time' ? 'auto' : '100%',
                                flex: formTimeType === 'time' ? '1' : 'none',
                                fontSize: '14px',
                                height: '40px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                textAlign: 'left',
                                padding: '0',
                                margin: '0',
                                color: isDarkMode ? '#fff' : '#333',
                              }}
                              placeholder="Fim do evento"
                              format="DD/MM/YYYY"
                              status={validationErrors.endDate ? 'error' : undefined}
                              suffixIcon={<CalendarOutlined />}
                              bordered={!isDarkMode}
                            />
                            {formTimeType === 'time' && (
                              <DatePicker
                                value={formEndTime}
                                onChange={(value) => {
                                  setFormEndTime(value);
                                  if (validationErrors.endTime) {
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      endTime: undefined,
                                    }));
                                  }
                                }}
                                style={{
                                  width: '80px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  fontSize: '14px',
                                  height: '40px',
                                  boxShadow: 'none',
                                }}
                                format="HH:mm"
                                picker="time"
                                status={validationErrors.endTime ? 'error' : undefined}
                                bordered
                                placeholder="Hora"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          marginTop: 8,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 0px',
                          }}
                        >
                          <Select
                            size="middle"
                            value={formTimeType}
                            onChange={(value) => {
                              setFormTimeType(value);

                              if (value !== 'time') {
                                setFormStartTime(dayjs().hour(0).minute(0)); // 00:00
                                setFormEndTime(dayjs().hour(23).minute(59)); // 23:59

                                if (validationErrors.startTime) {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    startTime: undefined,
                                  }));
                                }
                                if (validationErrors.endTime) {
                                  setValidationErrors((prev) => ({ ...prev, endTime: undefined }));
                                }
                              }
                            }}
                            style={{ minWidth: 250 }}
                            options={[
                              { value: 'time', label: 'Horário' },
                              { value: 'before_market', label: 'Antes da abertura do mercado' },
                              { value: 'after_market', label: 'Após o fechamento do mercado' },
                              { value: 'full_day', label: 'Dia inteiro' },
                            ]}
                          />
                        </div>
                      </div>

                      {validationErrors.date && (
                        <Typography.Text
                          type="danger"
                          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                        >
                          {validationErrors.date}
                        </Typography.Text>
                      )}
                      {validationErrors.endDate && (
                        <Typography.Text
                          type="danger"
                          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                        >
                          {validationErrors.endDate}
                        </Typography.Text>
                      )}
                    </div>
                    {validationErrors.startTime && (
                      <Typography.Text
                        type="danger"
                        style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                      >
                        {validationErrors.startTime}
                      </Typography.Text>
                    )}
                    {validationErrors.endTime && (
                      <Typography.Text
                        type="danger"
                        style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                      >
                        {validationErrors.endTime}
                      </Typography.Text>
                    )}
                  </Space>
                ),
              },
              ...(shouldShowAcionistaTab
                ? [
                    {
                      key: 'acionista',
                      label: 'Acionista',
                      children: (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input.Search
                            placeholder="Pesquisar participantes"
                            allowClear
                            value={searchShareholderTerm}
                            onChange={(e) => {
                              setSearchShareholderTerm(e.target.value);
                              setShareholderFiltersParams((prev) => ({ ...prev, page: 1 }));
                            }}
                            style={{ marginBottom: 16 }}
                          />
                          <Table<ListShareholderWithGroupSchema>
                            size="small"
                            columns={[
                              {
                                title: 'Nome',
                                dataIndex: 'name',
                                key: 'name',
                                render: (name: string | null) => name || 'Nome não informado',
                              },
                            ]}
                            dataSource={shareholders}
                            rowSelection={{
                              type: 'checkbox',
                              selectedRowKeys: selectedShareholders,
                              onChange: handleShareholderSelection,
                            }}
                            rowKey="id"
                            loading={shareholdersWithGroupQuery.isLoading}
                            pagination={{
                              current: shareholderFiltersParams.page,
                              pageSize: shareholderFiltersParams.size,
                              total: shareholdersWithGroupQuery.data?.data?.totalItems || 0,
                              defaultPageSize: 15,
                              showSizeChanger: true,
                              pageSizeOptions: [`15`, `30`, `50`, `100`],
                              position: [`bottomRight`],
                              onChange: handlePageChange,
                              onShowSizeChange: handlePageChange,
                            }}
                          />
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Drawer>

        <Modal
          open={isMarkerModalOpen}
          onCancel={() => setIsMarkerModalOpen(false)}
          onOk={addMarker}
          title="Criar agenda"
          okText="Criar"
          cancelText="Cancelar"
          okButtonProps={{ disabled: !newMarkerName.trim() }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>Nome da agenda</Typography.Text>
              <Input
                placeholder="Ex: Reuniões importantes"
                value={newMarkerName}
                onChange={(e) => setNewMarkerName(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>
            <div>
              <Typography.Text strong>Cor</Typography.Text>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: newMarkerColor,
                    border: `2px solid ${BORDER}`,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <input
                    type="color"
                    value={newMarkerColor}
                    onChange={(e) => setNewMarkerColor(e.target.value)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      border: 'none',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                    }}
                  />
                </div>
                <Typography.Text style={{ fontSize: 14, color: '#666' }}>
                  {newMarkerColor}
                </Typography.Text>
              </div>
            </div>
          </Space>
        </Modal>

        <Modal
          title="Editar agenda"
          open={isEditMarkerModalOpen}
          onOk={updateMarker}
          onCancel={() => {
            setIsEditMarkerModalOpen(false);
            setEditingMarker(null);
          }}
          okText="Salvar"
          cancelText="Cancelar"
          okButtonProps={{ disabled: !editMarkerName.trim() }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>Nome da agenda</Typography.Text>
              <Input
                placeholder="Ex: Reuniões importantes"
                value={editMarkerName}
                onChange={(e) => setEditMarkerName(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>
            <div>
              <Typography.Text strong>Cor</Typography.Text>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: editMarkerColor,
                    border: `2px solid ${BORDER}`,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <input
                    type="color"
                    value={editMarkerColor}
                    onChange={(e) => setEditMarkerColor(e.target.value)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      border: 'none',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                    }}
                  />
                </div>
                <Typography.Text style={{ fontSize: 14, color: '#666' }}>
                  {editMarkerColor}
                </Typography.Text>
              </div>
            </div>
          </Space>
        </Modal>

        <Modal
          open={isDeleteModalOpen}
          onCancel={handleCancelDelete}
          footer={null}
          closable={false}
          width={552}
          style={{
            paddingBottom: 0,
          }}
        >
          <div className="custom-delete-modal">
            {/* Header */}
            <div className="custom-delete-modal-header">
              <Typography.Text
                style={{
                  fontSize: '16px',
                  color: '#262626',
                  lineHeight: '24px',
                }}
              >
                Excluir evento
              </Typography.Text>
              <CloseItemIcon
                onClick={handleCancelDelete}
                style={{
                  cursor: 'pointer',
                  width: '24px',
                  height: '24px',
                }}
              />
            </div>

            {/* Content */}
            <div className="custom-delete-modal-content">
              <div className="custom-delete-modal-icon">
                <ExclamationCircleOutlined
                  style={{
                    fontSize: '48px',
                    color: '#ff4d32',
                  }}
                />
              </div>
              <div className="custom-delete-modal-info">
                <Typography.Text
                  strong
                  style={{
                    fontSize: '14px',
                    color: '#262626',
                    lineHeight: '22px',
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  Tem certeza que deseja excluir?
                </Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    color: '#8c8c8c',
                    lineHeight: '22px',
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  Você está prestes a excluir o evento &quot;{eventToDelete?.title}&quot;. Esta ação
                  não pode ser desfeita.
                </Typography.Text>
              </div>
            </div>

            {/* Footer */}
            <div className="custom-delete-modal-footer">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleCancelDelete}
                className="custom-delete-back-button"
              >
                Voltar
              </Button>
              <Button
                danger
                type="primary"
                loading={isDeletingEvent}
                disabled={isDeletingEvent}
                onClick={confirmDeleteEvent}
                style={{
                  height: '36px',
                  borderRadius: '6px',
                  fontWeight: 500,
                  backgroundColor: '#ff4d32',
                  borderColor: '#ff4d32',
                }}
              >
                Desejo excluir
              </Button>
            </div>
          </div>
        </Modal>

        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleCreateEvent}
          className="floating-add-button"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 84, 98, 0.15)',
          }}
        />
      </Content>
    </Layout>
  );
};
